import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { AdminSignup, AdminSignupListItem, SignupConversion, SignupsFilter } from '../types';

export type AdminSignupsPage = {
  items: AdminSignupListItem[];
  count: number;
};

export type SignupsPeriod = {
  startDate: Dayjs;
  endDate: Dayjs;
};

// The end of the period is exclusive on the server, so the day the admin picked is added on here — the same
// way the message-error reports do it. Without it the period silently stops at midnight of its last day.
const periodParams = (period: SignupsPeriod) => ({
  startDateInterval: period.startDate.format('YYYY-MM-DD'),
  endDateInterval: period.endDate.add(1, 'day').format('YYYY-MM-DD'),
});

const list = (values: string[]) => (values.length > 0 ? values.join(',') : undefined);

// Everything that narrows or orders the funnels and nothing that pages them: the list and the export must
// mean the same funnels, and the export answers 400 to `limit` or `offset`.
const filterParams = (filter: SignupsFilter, period: SignupsPeriod | null) => ({
  ...(period == null ? {} : periodParams(period)),
  query: filter.query.length > 0 ? filter.query : undefined,
  statuses: list(filter.statuses),
  steps: list(filter.steps),
  applicationIds: list(filter.applicationIds),
  source: filter.source.length > 0 ? filter.source : undefined,
  sort: filter.sort,
});

export function useAdminSignupsApi() {
  return useMemo(
    () => ({
      // A null period is every funnel there has ever been, which is what a search by text asks for.
      searchSignups: (filter: SignupsFilter, period: SignupsPeriod | null) =>
        apiClient.get<AdminSignupsPage>('/signups', {
          params: { ...filterParams(filter, period), limit: filter.limit, offset: filter.offset },
        }),

      exportSignups: (filter: SignupsFilter, period: SignupsPeriod | null) => {
        // The screen reports the failure in its own words; the generic toast would silence it.
        const config: CustomAxiosRequestConfig = {
          params: filterParams(filter, period),
          responseType: 'blob',
          skipGenericToast: true,
        };
        return apiClient.get<Blob>('/signups/export', config);
      },

      getConversion: (period: SignupsPeriod, applicationId?: string) =>
        apiClient.get<SignupConversion>('/signups/conversion', {
          params: { ...periodParams(period), applicationId: applicationId },
        }),

      getSignup: (signupId: string) => apiClient.get<AdminSignup>(`/signups/${signupId}`),
    }),
    [],
  );
}
